/**
 * IOH Excel Export - Constants & Color Mappings
 * Centralized color definitions for Excel export styling
 */

// ============================================
// TRAIN STATES & STATUS
// ============================================
export const TRAIN_MOVEMENT_STATE = {
  STARTED: 'TRAIN_MOVEMENT_STARTED',
  STOPPED: 'TRAIN_MOVEMENT_STOPPED'
};

export const TRAIN_STATUS_STATE = {
  ORIGINATED: 'TRAIN_STATE_ORIGINATED',
  AT_ORIGIN: 'TRAIN_STATE_AT_ORIGIN',
  IN_TRANSIT: 'TRAIN_STATE_IN_TRANSIT',
  AT_DESTINATION: 'TRAIN_STATE_AT_DESTINATION'
};

// ============================================
// COLORS FOR EXCEL EXPORT
// ============================================
export const EXCEL_TRAIN_STOP_COLOR = {
  LESS_THAN_2_HOURS: '#ffc042',      // Orange
  MORE_THAN_2_HOURS: '#ed4545'       // Red
};

export const EXCEL_CREW_ON_DUTY_COLOR = {
  CREW_ODTOD_GREEN: '#27AE60',       // ≤45 minutes
  CREW_ODTOD_YELLOW: '#F39C12',      // 45-90 minutes
  CREW_ODTOD_RED: '#E74C3C',         // ≥90 minutes
  MORE_THAN_OR_EQUAL_TO_EIGHT_HOUR: '#3498DB',  // ≥8 hours
  LESS_THAN_EIGHT_HOUR: '#E74C3C'    // <8 hours
};

export const PERFORMANCE_COLOR_MAP = {
  EXCELLENT: '#27AE60',  // ≥80%
  GOOD: '#F39C12',       // 60-79%
  POOR: '#E74C3C'        // <60%
};

// ============================================
// LABEL MAPPINGS
// ============================================
export const TRAIN_STATE_LABEL_MAP = {
  'TRAIN_STATE_ORIGINATED': 'Originated',
  'TRAIN_STATE_AT_ORIGIN': 'At Origin',
  'TRAIN_STATE_IN_TRANSIT': 'In Transit',
  'TRAIN_STATE_AT_DESTINATION': 'At Destination'
};

export const TRAIN_MOVEMENT_LABEL_MAP = {
  'TRAIN_MOVEMENT_STARTED': 'Moving',
  'TRAIN_MOVEMENT_STOPPED': 'Stopped'
};

export const LOR_INDICATOR_STATUS = {
  R: 'Crew Requested',
  NR: 'Crew Not Requested',
  C: 'Crew Called',
  NC: 'Crew Requested Not Called',
  D: 'Next Crew On-Duty'
};

export const LOR_INDICATOR_COLOR = {
  R: '#F39C12',   // Orange
  NR: '#E74C3C',  // Red
  C: '#27AE60',   // Green
  NC: '#F39C12',  // Orange
  D: '#3498DB'    // Blue
};

// ============================================
// FORMAT CONSTANTS
// ============================================
export const TIME_FORMAT = {
  DATE_TIME: 'MM/DD/YYYY HH:mm',
  DATE_TIME_SECONDS: 'MM/DD/YYYY HH:mm:ss',
  DATE_ONLY: 'MM/DD/YYYY',
  TIME_ONLY: 'HH:mm',
  TIME_ONLY_SECONDS: 'HH:mm:ss',
  ISO_FORMAT: 'YYYY-MM-DDTHH:mm:ss.SSSZ'
};

// ============================================
// DURATION THRESHOLDS (IN MINUTES)
// ============================================
export const DURATION_THRESHOLDS = {
  ODTOD_GOOD_MAX: 45,        // ≤45 mins is green
  ODTOD_WARNING_MIN: 45,
  ODTOD_WARNING_MAX: 90,     // 45-90 mins is yellow
  ODTOD_CRITICAL_MIN: 90,    // ≥90 mins is red
  STOPPED_TIME_WARNING: 120,  // 2 hours
  HOS_MINIMUM: 480           // 8 hours
};

// ============================================
// COLUMN WIDTH & FONT CONSTANTS
// ============================================
export const EXCEL_COLUMN_WIDTHS = {
  NARROW: 8,
  SMALL: 12,
  MEDIUM: 15,
  LARGE: 18,
  EXTRA_LARGE: 20,
  XXL: 25
};

export const EXCEL_FONT = {
  NAME: 'Calibri',
  SIZE: 11,
  SIZE_HEADER: 12,
  COLOR_HEADER: 'FFFFFF',
  COLOR_TEXT: '000000'
};

// ============================================
// COLOR PALETTE - EXTENDED
// ============================================
export const COLOR_PALETTE = {
  success: '#27AE60',
  warning: '#F39C12',
  danger: '#E74C3C',
  info: '#3498DB',
  white: '#FFFFFF',
  lightGray: '#ECF0F1',
  mediumGray: '#BDC3C7',
  darkGray: '#95A5A6',
  black: '#000000',
  headerBg: '#366092'
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get color based on ODTOD minutes
 */
export function getODTODColor(minutes: number | string): string {
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  
  if (isNaN(mins)) return COLOR_PALETTE.darkGray;
  
  if (mins <= DURATION_THRESHOLDS.ODTOD_GOOD_MAX) {
    return EXCEL_CREW_ON_DUTY_COLOR.CREW_ODTOD_GREEN;
  } else if (mins < DURATION_THRESHOLDS.ODTOD_CRITICAL_MIN) {
    return EXCEL_CREW_ON_DUTY_COLOR.CREW_ODTOD_YELLOW;
  } else {
    return EXCEL_CREW_ON_DUTY_COLOR.CREW_ODTOD_RED;
  }
}

/**
 * Get color based on stopped time
 */
export function getTrainStoppedTimeColor(minutes: number | string): string {
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  
  if (isNaN(mins)) return COLOR_PALETTE.white;
  
  if (mins > DURATION_THRESHOLDS.STOPPED_TIME_WARNING) {
    return EXCEL_TRAIN_STOP_COLOR.MORE_THAN_2_HOURS;
  } else {
    return EXCEL_TRAIN_STOP_COLOR.LESS_THAN_2_HOURS;
  }
}

/**
 * Get color based on HOS
 */
export function getHOSColor(minutes: number | string): string {
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  
  if (isNaN(mins)) return COLOR_PALETTE.darkGray;
  
  if (mins >= DURATION_THRESHOLDS.HOS_MINIMUM) {
    return EXCEL_CREW_ON_DUTY_COLOR.MORE_THAN_OR_EQUAL_TO_EIGHT_HOUR;
  } else {
    return EXCEL_CREW_ON_DUTY_COLOR.LESS_THAN_EIGHT_HOUR;
  }
}

/**
 * Get performance color based on percentage
 */
export function getPerformanceColor(percentage: number | string): string {
  const perf = typeof percentage === 'string' ? parseInt(percentage, 10) : percentage;
  
  if (isNaN(perf)) return COLOR_PALETTE.darkGray;
  
  if (perf >= 80) {
    return PERFORMANCE_COLOR_MAP.EXCELLENT;
  } else if (perf >= 60) {
    return PERFORMANCE_COLOR_MAP.GOOD;
  } else {
    return PERFORMANCE_COLOR_MAP.POOR;
  }
}

/**
 * Get color based on the performance status.
 */
export function getPerformanceStatusColor(status: string): string | undefined {
  const normalizedStatus = String(status || '').trim().toUpperCase();

  if (normalizedStatus.includes('LATE')) {
    return COLOR_PALETTE.danger;
  }

  if (normalizedStatus.includes('DELAYED')) {
    return COLOR_PALETTE.warning;
  }

  if (normalizedStatus.includes('AHEAD_OF_TIME') || normalizedStatus === 'AOT') {
    return COLOR_PALETTE.success;
  }

  if (normalizedStatus.includes('EARLY')) {
    return COLOR_PALETTE.info;
  }

  return undefined;
}
/**
 * Get LOR status label
 */
export function getLORStatusLabel(indicator: string): string {
  return LOR_INDICATOR_STATUS[indicator] || 'Unknown';
}

/**
 * Get LOR status color
 */
export function getLORStatusColor(indicator: string): string {
  return LOR_INDICATOR_COLOR[indicator] || COLOR_PALETTE.darkGray;
}

/**
 * Convert minutes to readable duration format (e.g., "2h 30m")
 */
export function formatDuration(minutes: number | string): string {
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  
  if (isNaN(mins) || mins < 0) return '-';
  
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  
  if (hours === 0) {
    return `${remainingMins}m`;
  } else if (remainingMins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMins}m`;
  }
}

/**
 * Convert hex color to ARGB (Excel format)
 */
export function hexToArgb(hex: string): string {
  if (!hex || hex === '-') return 'FF000000';
  const cleanHex = hex.replace('#', '');
  return 'FF' + cleanHex.toUpperCase();
}
